import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Link } from "@tanstack/react-router";
import { usd } from "@/lib/pricing";

type Car = {
  make: string;
  model: string;
  daily_rate: number | string;
  security_deposit: number | string;
  location: string | null;
  weekly_discount_pct: number | string;
  monthly_discount_pct: number | string;
};

export function VehicleFaq({
  car,
  businessHours,
}: {
  car: Car;
  businessHours?: string | null;
}) {
  const name = `${car.make} ${car.model}`;
  const rate = usd(Number(car.daily_rate));
  const deposit = Number(car.security_deposit) > 0
    ? usd(Number(car.security_deposit))
    : "starting at $300";
  const location = car.location || "Miami";
  const weekly = Number(car.weekly_discount_pct) || 0;
  const monthly = Number(car.monthly_discount_pct) || 0;
  const discountLine =
    weekly > 0 || monthly > 0
      ? `Yes — save ${weekly > 0 ? `${weekly}% on 7+ days` : ""}${
          weekly > 0 && monthly > 0 ? " and " : ""
        }${monthly > 0 ? `${monthly}% on 30+ days` : ""}. Ask us about custom monthly pricing.`
      : "Ask us about weekly and monthly rates — we offer custom pricing for longer stays.";

  const items: { q: string; a: string }[] = [
    {
      q: `How much does the ${name} cost to rent?`,
      a: `The ${name} is ${rate} per day, with a refundable security deposit of ${deposit} held on your card at pickup and released after the vehicle is returned in the same condition.`,
    },
    {
      q: "Are weekly or monthly discounts available?",
      a: discountLine,
    },
    {
      q: "How old do I need to be to rent?",
      a: "Drivers must be 25 or older with a valid driver's license and full-coverage auto insurance that extends to rental vehicles.",
    },
    {
      q: `Where can I pick up or get this car delivered?`,
      a: `Pickup is based in ${location}. We deliver throughout Miami-Dade and Broward — including MIA and FLL airports, hotels, and private residences. Text us your address for an instant delivery quote.`,
    },
    {
      q: "What's included with the rental?",
      a: "Comprehensive insurance, 24/7 roadside assistance, and local delivery across Miami-Dade are included. Tolls, traffic citations, cleaning, and late-return fees are billed separately.",
    },
    {
      q: "What hours can I pick up or return the vehicle?",
      a: businessHours
        ? `Our standard hours are ${businessHours.replace(/\n/g, " · ")}. After-hours pickup and drop-off can be arranged in advance.`
        : "After-hours pickup and drop-off can be arranged in advance — just let us know your flight or schedule.",
    },
    {
      q: "Can I extend my rental once I have the car?",
      a: "Absolutely. Call or text us at least 24 hours before your scheduled return and we'll extend the agreement at your existing daily rate, subject to availability.",
    },
  ];

  return (
    <section className="mx-auto max-w-7xl px-6 pb-16">
      <div className="rounded-lg border border-border bg-card p-6 md:p-8">
        <div className="text-xs uppercase tracking-[0.25em] text-accent">FAQ</div>
        <h2 className="mt-2 font-display text-2xl text-espresso md:text-3xl">
          About renting the {name}
        </h2>
        <Accordion type="single" collapsible className="mt-4">
          {items.map((it, i) => (
            <AccordionItem key={i} value={`vfaq-${i}`}>
              <AccordionTrigger className="text-left">{it.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                {it.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <div className="mt-5 text-sm">
          <Link
            to="/faq"
            className="text-accent underline underline-offset-4 hover:text-accent/80"
          >
            See the full FAQ →
          </Link>
        </div>
      </div>
    </section>
  );
}
